import {
  Add as AddIcon,
  Support as SupportIcon,
  Assignment as TicketIcon,
} from '@mui/icons-material';
import { Box, Divider, Link, Paper, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useState } from 'react';
import { useAccount } from '../contexts/AccountContext';

import { SubmitTicketForm } from './Help/SubmitTicketForm';
import { TicketDialog } from './Help/TicketDialog';
import { TicketList } from './Help/TicketList';
import { useHelpTickets } from './Help/useHelpTickets';

const HelpPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('tickets');
  const theme = useTheme();
  const { hasPermission } = useAccount();

  const {
    tickets,
    loading,
    responseLoading,
    openDialog,
    setOpenDialog,
    selectedTicket,
    readTickets,
    ticketForm,
    setTicketForm,
    responseForm,
    setResponseForm,
    handleTicketClick,
    handleAddResponse,
    submitNewTicket,
  } = useHelpTickets(activeTab);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string): void => {
    setActiveTab(newValue);
  };

  const handleCreateTicket = (e: React.FormEvent) => {
    submitNewTicket(e, setActiveTab);
  };

  return (
    <PageContainer title="Help & Support">
      <Typography variant="body1" gutterBottom>
        If you need assistance, you can submit a ticket or contact us directly via email or phone.
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Paper sx={{ p: 2, border: 'none' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                fontSize: '1rem',
                fontWeight: 400,
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                mr: 1,
                py: 1,
                color: 'text.primary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                  borderBottom: '4px solid',
                  borderColor: theme.palette.bidlogiq.brick,
                  marginBottom: '-1px',
                },
                '& .MuiTab-iconWrapper': {
                  fontSize: '1.5rem',
                },
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
          >
            <Tab label="My Tickets" value="tickets" icon={<TicketIcon />} iconPosition="start" />
            <Tab label="Submit Ticket" value="submit" icon={<AddIcon />} iconPosition="start" />
            <Tab label="Contact Us" value="contact" icon={<SupportIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {activeTab === 'contact' && (
          <Box>
            <Typography>
              <strong>Email:</strong>{' '}
              <Link href="mailto:support@bidlogiq.ai">support@bidlogiq.ai</Link>
              <br />
              <strong>AUS:</strong>+61 (0)7 2143 6035
              <br />
              <strong>USA/WhatsApp:</strong> +1 650 746 4687
              <br />
            </Typography>
          </Box>
        )}

        {activeTab === 'submit' && (
          <SubmitTicketForm
            ticketForm={ticketForm}
            setTicketForm={setTicketForm}
            loading={loading}
            onSubmit={handleCreateTicket}
          />
        )}

        {activeTab === 'tickets' && (
          <Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
            />
            <TicketList
              tickets={tickets}
              loading={loading}
              readTickets={readTickets}
              hasPermission={hasPermission}
              onTicketClick={handleTicketClick}
            />
          </Box>
        )}

        <TicketDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          ticket={selectedTicket}
          responseForm={responseForm}
          setResponseForm={setResponseForm}
          responseLoading={responseLoading}
          onAddResponse={handleAddResponse}
        />
      </Paper>
    </PageContainer>
  );
};

export default HelpPage;
